import type { CorridorStat, Product, TransactionEvent, WalletStat } from "./types";

/**
 * Rolling aggregates — corridor volumes, 24h volume, TPS.
 *
 * Memory-bounded: events older than the longest window are dropped.
 */
export class Aggregator {
  private readonly window24hMs = 24 * 60 * 60 * 1000;
  private readonly window60mMs = 60 * 60 * 1000;
  private readonly windowTpsMs = 10_000;

  private history: {
    ts: number;
    amountUsd: number;
    corridor: string;
    product: Product;
  }[] = [];

  push(ev: TransactionEvent) {
    this.history.push({
      ts: ev.ts,
      amountUsd: ev.amountUsd,
      corridor: `${ev.origin.country}->${ev.dest.country}`,
      product: ev.product,
    });
    this.gc(ev.ts);
  }

  private gc(now: number) {
    const cutoff = now - this.window24hMs;
    // Since events arrive ~monotonically, shift from the front.
    while (this.history.length && this.history[0].ts < cutoff) {
      this.history.shift();
    }
  }

  tps(now = Date.now()): number {
    const cutoff = now - this.windowTpsMs;
    let n = 0;
    for (let i = this.history.length - 1; i >= 0; i--) {
      if (this.history[i].ts < cutoff) break;
      n++;
    }
    return n / (this.windowTpsMs / 1000);
  }

  /** Top corridors from send_money only — the actual cross-border story. */
  topCorridors(limit = 3, now = Date.now()): CorridorStat[] {
    const cutoff = now - this.window60mMs;
    const map = new Map<string, CorridorStat>();
    for (let i = this.history.length - 1; i >= 0; i--) {
      const e = this.history[i];
      if (e.ts < cutoff) break;
      if (e.product !== "send_money" && e.product !== "card") continue;
      const [a, b] = e.corridor.split("->");
      const existing = map.get(e.corridor) ?? {
        key: e.corridor,
        countryA: a,
        countryB: b,
        volumeUsd60m: 0,
        count60m: 0,
      };
      existing.volumeUsd60m += e.amountUsd;
      existing.count60m += 1;
      map.set(e.corridor, existing);
    }
    return [...map.values()].sort((a, b) => b.volumeUsd60m - a.volumeUsd60m).slice(0, limit);
  }

  /** Rolling 60-min count + volume per product. Drives the wallet panel. */
  walletStats(now = Date.now()): Record<Product, WalletStat> {
    const cutoff = now - this.window60mMs;
    const init: Product[] = [
      "send_money",
      "add_money",
      "exchange_money",
      "card",
      "other",
    ];
    const out = Object.fromEntries(
      init.map((p) => [p, { product: p, count60m: 0, volumeUsd60m: 0 }]),
    ) as Record<Product, WalletStat>;

    for (let i = this.history.length - 1; i >= 0; i--) {
      const e = this.history[i];
      if (e.ts < cutoff) break;
      out[e.product].count60m += 1;
      out[e.product].volumeUsd60m += e.amountUsd;
    }
    return out;
  }

  /**
   * P99 amount for spotlight threshold — tuned to Eversend live data where
   * P50 ≈ $8, P99 ≈ $545, max ≈ $21k. Floor at $500 prevents cold-start
   * spotlighting of trivial amounts on the first few events.
   */
  spotlightThreshold(now = Date.now()): number {
    const cutoff = now - this.window24hMs;
    const amounts: number[] = [];
    for (const e of this.history) if (e.ts >= cutoff) amounts.push(e.amountUsd);
    if (amounts.length < 50) return 500;
    amounts.sort((a, b) => a - b);
    const p99 = amounts[Math.floor(amounts.length * 0.99)];
    return Math.max(500, p99);
  }
}
